import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6b68fc69dc5c22f0f3d72917cf6dd771.jpg"
            alt="About Vantex Construction"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6"
          >
            ABOUT US
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-neutral-lighter-gray max-w-3xl mx-auto"
          >
            Your trusted partner for residential construction in Dallas-Fort Worth
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-primary-red text-sm font-bold tracking-widest mb-4">
                WHO WE ARE
              </p>
              <h2 className="text-4xl md:text-5xl font-black text-neutral-charcoal mb-6">
                BUILDING QUALITY SINCE DAY ONE
              </h2>
              <div className="space-y-4 text-lg text-neutral-gray leading-relaxed">
                <p>
                  <strong className="text-neutral-charcoal font-bold">Vantex Construction LLC</strong> is a licensed and insured residential contractor serving the Dallas-Fort Worth metroplex. We specialize in outdoor structures, concrete work, fences, and interior remodeling.
                </p>
                <p>
                  Our commitment is simple: deliver professional construction work with transparent pricing, clear communication, and reliable service. Every project is backed by proper licensing, comprehensive insurance, and workmanship warranties.
                </p>
                <p>
                  We're not a large corporate operation. We're a local contractor who takes pride in our work and stands behind every project we complete. When you call, you'll talk to someone who actually works on your project.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-lg overflow-hidden shadow-2xl"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20construction%20contractor%20reviewing%20blueprints%20and%20plans%20at%20residential%20job%20site%2C%20Dallas%20Texas%20setting%2C%20construction%20materials%20and%20tools%20visible%2C%20high%20quality%20photography%20showing%20attention%20to%20detail%20and%20professionalism%2C%20simple%20clean%20background&width=1200&height=800&seq=about-story-vantex-29&orientation=landscape"
                alt="Vantex Construction Team"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-neutral-black py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              WHAT SETS US APART
            </h2>
            <p className="text-lg text-neutral-light-gray max-w-3xl mx-auto">
              Our commitment to quality, transparency, and customer satisfaction
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shield-check-fill',
                title: 'Licensed & Insured',
                description: 'Fully licensed contractor with comprehensive insurance coverage for your protection and peace of mind.',
              },
              {
                icon: 'ri-map-pin-fill',
                title: 'Local to DFW',
                description: 'Based in Dallas-Fort Worth, we understand local building codes, weather conditions, and homeowner needs.',
              },
              {
                icon: 'ri-award-fill',
                title: 'Warranty Protection',
                description: 'Every project backed by our comprehensive workmanship warranty. We stand behind our work.',
              },
              {
                icon: 'ri-file-list-3-fill',
                title: 'Clear Estimates',
                description: 'Detailed, written estimates with no hidden fees or surprise charges. You know exactly what to expect.',
              },
              {
                icon: 'ri-team-fill',
                title: 'Professional Team',
                description: 'Experienced craftsmen who take pride in their work and treat your property with respect.',
              },
              {
                icon: 'ri-customer-service-2-fill',
                title: 'Reliable Service',
                description: 'Clear communication, on-time completion, and responsive customer service throughout your project.',
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-neutral-charcoal p-8 rounded-lg"
              >
                <div className="w-16 h-16 bg-primary-red rounded-lg flex items-center justify-center mb-6">
                  <i className={`${value.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{value.title}</h3>
                <p className="text-neutral-light-gray leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-neutral-charcoal mb-4">
              SERVING DALLAS-FORT WORTH
            </h2>
            <p className="text-lg text-neutral-gray max-w-3xl mx-auto">
              Proudly serving homeowners throughout the DFW metroplex
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
            {[
              'Dallas', 'Fort Worth', 'Plano', 'Frisco',
              'McKinney', 'Allen', 'Richardson', 'Carrollton',
              'Lewisville', 'The Colony', 'Flower Mound', 'Coppell',
              'Grapevine', 'Prosper', 'Celina', 'Little Elm',
            ].map((city, index) => (
              <motion.div
                key={city}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-neutral-bg-gray p-4 rounded-lg text-center"
              >
                <i className="ri-map-pin-line text-primary-red text-2xl mb-2 w-6 h-6 flex items-center justify-center mx-auto"></i>
                <p className="text-neutral-charcoal font-semibold">{city}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-neutral-gray mb-6">
              Don't see your city? Contact us to confirm service availability in your area.
            </p>
            <Link
              to="/quote"
              className="inline-flex items-center bg-primary-red hover:bg-primary-red-dark text-white px-8 py-4 font-bold text-base tracking-wide transition-colors whitespace-nowrap cursor-pointer"
            >
              REQUEST A QUOTE
              <i className="ri-arrow-right-line ml-2 text-xl w-5 h-5 flex items-center justify-center"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-neutral-black py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            LET'S BUILD SOMETHING TOGETHER
          </h2>
          <p className="text-xl text-neutral-light-gray mb-10 max-w-2xl mx-auto">
            Contact us today to discuss your project and get a free estimate
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/quote"
              className="inline-flex items-center justify-center bg-primary-red hover:bg-primary-red-dark text-white px-10 py-5 font-bold text-lg tracking-wide transition-colors whitespace-nowrap cursor-pointer"
            >
              REQUEST FREE ESTIMATE
              <i className="ri-arrow-right-line ml-3 text-2xl w-6 h-6 flex items-center justify-center"></i>
            </Link>
            <a
              href="tel:+14692544779"
              className="inline-flex items-center justify-center border-2 border-white hover:bg-white hover:text-neutral-black text-white px-10 py-5 font-bold text-lg tracking-wide transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-3 text-2xl w-6 h-6 flex items-center justify-center"></i>
              (469) 254-4779
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;